import React, { useState, useCallback, useEffect } from 'react';
import { useDashboard } from '../../contexts/DashboardContext';
import { useConfig } from '../../contexts/ConfigContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const DashboardManager = ({
  dashboards,
  setDashboards,
  setError,
  handleQueryResult,
  setIsDashboardLoading,
  currentUser,
  isAdmin,
  saveQueryTitle,
}) => {
  // Dashboard view mode state - will be saved to Firestore
  const [dashboardViewMode, setDashboardViewMode] = useState('list');

  // Save view mode preference to Firestore
  const updateDashboardViewMode = useCallback(async (mode) => {
    setDashboardViewMode(mode);
    
    // Save to user preferences in Firestore
    if (currentUser?.email) {
      try {
        const userPrefsRef = doc(db, 'registeredUsersPreferences', currentUser.email);
        await updateDoc(userPrefsRef, {
          dashboardViewMode: mode,
          lastUpdated: new Date()
        });
      } catch (error) {
        console.error('Error saving dashboard view mode:', error);
      }
    }
  }, [currentUser]);

  const { selectedDashboard, updateSelectedDashboard, clearSelectedDashboard } = useDashboard();
  const { config, isLoading: isConfigLoading } = useConfig();

  // Migration function
  const migrateLocalStorageToFirestore = useCallback(async () => {
    if (!currentUser?.email) return;
    
    try {
      // Check if we've already migrated for this user
      const migrationKey = `dashboard_settings_migrated_${currentUser.email}`;
      if (localStorage.getItem(migrationKey)) {
        return; // Already migrated
      }
      
      // Get existing localStorage preferences
      const savedPreferences = localStorage.getItem('dashboardPreferences');
      if (!savedPreferences) {
        // No settings to migrate, mark as complete
        localStorage.setItem(migrationKey, 'true');
        return;
      }
      
      const preferences = JSON.parse(savedPreferences);
      
      // Migrate settings for each dashboard
      for (const [dashboardId, dashboardPrefs] of Object.entries(preferences)) {
        if (dashboardPrefs.querySettings) {
          // Migrate each query's settings
          for (const [queryId, settings] of Object.entries(dashboardPrefs.querySettings)) {
            try {
              const queryRef = doc(db, 'SavedQueries', queryId);
              const queryDoc = await getDoc(queryRef);
              
              if (queryDoc.exists()) {
                const currentUserSettings = queryDoc.data().userSettings || {};
                
                // Merge with existing settings
                await updateDoc(queryRef, {
                  userSettings: {
                    ...currentUserSettings,
                    [currentUser.email]: {
                      ...settings,
                      _migratedFrom: 'localStorage',
                      _migratedAt: new Date()
                    }
                  }
                });
                
                console.log(`Migrated settings for query ${queryId}`);
              }
            } catch (queryError) {
              console.error(`Failed to migrate query ${queryId}:`, queryError);
              // Continue with other queries even if one fails
            }
          }
        }
      }
      
      // Mark migration as complete
      localStorage.setItem(migrationKey, 'true');
      console.log('Dashboard settings migration completed');
      
      // Optionally, clean up old localStorage data after successful migration
      // localStorage.removeItem('dashboardPreferences');
      
    } catch (error) {
      console.error('Migration failed:', error);
    }
  }, [currentUser]);

  // Load dashboard view mode from Firestore when component mounts
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!currentUser?.email) return;
      
      try {
        const userPrefsRef = doc(db, 'registeredUsersPreferences', currentUser.email);
        const docSnap = await getDoc(userPrefsRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.dashboardViewMode) {
            setDashboardViewMode(data.dashboardViewMode);
          }
        }
      } catch (error) {
        console.error('Error loading user preferences:', error);
      }
    };
    
    loadUserPreferences();
  }, [currentUser]);


  const executeIndividualQuery = async (queryId, dashboardId) => {
    try {
      const response = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/handleDashboardOperation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: { 
            operation: 'executeQuery',
            reportData: { 
              queryId,
              dashboardId
            },
            userId: currentUser.email,
            isAdmin: isAdmin
          }
        }),
        credentials: 'include'
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const queryData = await response.json();
      return queryData;
    } catch (error) {
      throw error;
    }
  };
  

  // Function to handle saving card settings for a specific query

const handleSaveCardSettings = useCallback(async (queryId, settings) => {
  if (!selectedDashboard || !currentUser?.email) return;

  try {
    // Find the query in the current dashboard
    const query = selectedDashboard.queries.find(q => q.id === queryId);
    if (!query) return;

    // Use the same Firestore mechanism as library queries
    const queryRef = doc(db, 'SavedQueries', queryId);
    
    // Get the document to check current settings
    const queryDoc = await getDoc(queryRef);
    if (!queryDoc.exists()) {
      console.error('Query document not found:', queryId);
      return;
    }
    
    const queryData = queryDoc.data();
    // Get current userSettings or initialize
    const currentUserSettings = queryData.userSettings || {};
    
    // Update settings for this user
    const updatedUserSettings = {
      ...currentUserSettings,
      [currentUser.email]: {
        ...(currentUserSettings[currentUser.email] || {}),
        ...settings,
        _lastUpdated: new Date()
      }
    };
    
    // Prepare the update object
    const updateData = {
      userSettings: updatedUserSettings
    };
    
    // If the current user is the creator of the query, also update creatorSettings
    if (queryData.userId === currentUser.email) {
      updateData.creatorSettings = {
        ...settings,
        _lastUpdated: new Date()
      };
      console.log('Updating creator settings as well');
    }
    
    // Update Firestore
    await updateDoc(queryRef, updateData);
    
    console.log('Settings saved to Firestore:', queryId, settings);
    
    // Update local state to reflect changes immediately
    updateSelectedDashboard(current => ({
      ...current,
      queries: current.queries.map(q => 
        q.id === queryId 
          ? { 
              ...q, 
              userSettings: updatedUserSettings,
              creatorSettings: queryData.userId === currentUser.email ? updateData.creatorSettings : q.creatorSettings,
              cardSettings: {
                ...q.cardSettings,
                ...settings
              }
            }
          : q
      )
    }));
    
  } catch (error) {
    console.error('Error saving card settings:', error);
    setError('Failed to save settings. Please try again.');
  }
}, [selectedDashboard, currentUser, updateSelectedDashboard, setError]);

  // Function to fetch dashboards from backend
  const fetchDashboards = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    try {
      const response = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/handleDashboardOperation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: { 
            operation: 'get',
            userId: currentUser.email,
            isAdmin: isAdmin
          }
        })
      });
      const data = await response.json();
      setDashboards(data);
    } catch (error) {
      setError('Failed to fetch dashboards. Please try again later.');
    }
  }, [currentUser, setDashboards, setError, isAdmin]);

  useEffect(() => {
    if (currentUser && config) {
      // Run migration first, then fetch dashboards
      migrateLocalStorageToFirestore().then(() => {
        fetchDashboards();
      });
    }
  }, [currentUser, config, migrateLocalStorageToFirestore, fetchDashboards]);

  const handleSelectDashboard = useCallback(async (dashboard) => {
    setIsDashboardLoading(true);
    setError(null);
    try {
      // First, fetch dashboard structure
      const structureResponse = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/handleDashboardOperation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: { 
            operation: 'getDashboardStructure',
            reportData: { 
              dashboardId: dashboard.id,
              incrementRefreshCount: true
            },
            userId: currentUser.email,
            isAdmin: isAdmin
          }
        }),
        credentials: 'include'
      });
  
      if (!structureResponse.ok) {
        throw new Error(`HTTP error! status: ${structureResponse.status}`);
      }
  
      const dashboardStructure = await structureResponse.json();
      if (dashboardStructure.error) {
        throw new Error(dashboardStructure.error);
      }
  

      const queriesWithSettings = await Promise.all(
        dashboardStructure.queries.map(async (query) => {
          try {
            const queryRef = doc(db, 'SavedQueries', query.id);
            const queryDoc = await getDoc(queryRef);
            
            if (queryDoc.exists()) {
              const queryData = queryDoc.data();
              return {
                ...query,
                userSettings: queryData.userSettings || {},
                creatorSettings: queryData.creatorSettings || {},
                userId: queryData.userId // Include the creator's userId
              };
            }
            return query;
          } catch (error) {
            console.error(`Failed to fetch settings for query ${query.id}:`, error);
            return query;
          }
        })
      );

      // Initialize dashboard with loading states for each query
      const initializedQueries = queriesWithSettings.map(query => {
        // Get settings with proper fallback order:
        // 1. User-specific settings (if exists)
        // 2. Creator settings (if exists and user hasn't customized)
        // 3. Existing cardSettings from query
        // 4. Default settings
        
        const userSpecificSettings = query.userSettings?.[currentUser.email];
        const creatorSettings = query.creatorSettings;
        
        // If user has their own settings, use them. Otherwise, use creator settings as defaults
        const effectiveSettings = userSpecificSettings || creatorSettings || {};
        
        console.log(`Loading settings for query ${query.id}:`, {
          hasUserSettings: !!userSpecificSettings,
          hasCreatorSettings: !!creatorSettings,
          effectiveSettings
        });
        
        return {
          ...query,
          isLoading: true,
          result: null,
          schema: null,
          error: null,
          cardSettings: {
            viewMode: effectiveSettings.viewMode || query.cardSettings?.viewMode || 'table',
            chartType: effectiveSettings.chartType || query.cardSettings?.chartType || 'bar',
            selectedXAxis: effectiveSettings.selectedXAxis || query.cardSettings?.selectedXAxis || null,
            selectedYAxis: effectiveSettings.selectedYAxis || query.cardSettings?.selectedYAxis || null,
            syncScales: effectiveSettings.syncScales ?? query.cardSettings?.syncScales ?? false,
            sortColumn: effectiveSettings.sortColumn || query.cardSettings?.sortColumn || null,
            sortOrder: effectiveSettings.sortOrder || query.cardSettings?.sortOrder || 'asc',
            visibleColumns: effectiveSettings.visibleColumns || query.cardSettings?.visibleColumns || [],
            columnOrder: effectiveSettings.columnOrder || query.cardSettings?.columnOrder || [],
            cardWidth: effectiveSettings.cardWidth || query.cardSettings?.cardWidth || '100%',
            showSummary: effectiveSettings.showSummary ?? query.cardSettings?.showSummary ?? true,
            activeFilters: effectiveSettings.activeFilters || query.cardSettings?.activeFilters || {},
            showTrendLine: effectiveSettings.showTrendLine ?? query.cardSettings?.showTrendLine ?? true,
            isNumberFormatted: effectiveSettings.isNumberFormatted ?? query.cardSettings?.isNumberFormatted ?? true,
            transformationType: effectiveSettings.transformationType || query.cardSettings?.transformationType || null,
            pivotConfig: effectiveSettings.pivotConfig || query.cardSettings?.pivotConfig || null
          }
        };
      });
  
      // Create initial dashboard with all necessary properties
      const initialDashboard = {
        ...dashboard,
        ...dashboardStructure,
        id: dashboard.id,
        name: dashboard.name,
        userId: dashboard.userId,
        lastRefreshed: dashboardStructure.lastRefreshed || dashboard.lastRefreshed,
        refreshCount: dashboardStructure.refreshCount || dashboard.refreshCount,
        queries: initializedQueries
      };
  
      // Update the dashboard immediately with loading states
      updateSelectedDashboard(initialDashboard);
      setIsDashboardLoading(false);

      // Execute queries in parallel
      const queryPromises = initializedQueries.map(async (query) => {
        try {
          const queryResponse = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/handleDashboardOperation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              data: { 
                operation: 'executeQuery',
                reportData: { 
                  queryId: query.id,
                  dashboardId: dashboard.id
                },
                userId: currentUser.email,
                isAdmin: isAdmin
              }
            }),
            credentials: 'include'
          });

          const queryData = await queryResponse.json();
          
          // Update the dashboard with each query result as it arrives
          // while preserving existing card settings
          updateSelectedDashboard(current => ({
            ...current,
            queries: current.queries.map(q => {
              if (q.id === query.id) {
                const existingSettings = q.cardSettings || {};
                return {
                  ...q,
                  ...queryData,
                  isLoading: false,
                  error: queryData.error || null,
                  cardSettings: {
                    ...existingSettings,
                    // Only set these if they don't already exist
                    visibleColumns: existingSettings.visibleColumns?.length 
                      ? existingSettings.visibleColumns 
                      : Object.keys(queryData.result?.[0] || {}),
                    columnOrder: existingSettings.columnOrder?.length
                      ? existingSettings.columnOrder
                      : Object.keys(queryData.result?.[0] || {})
                  }
                };
              }
              return q;
            })
          }));

          return queryData;
        } catch (error) {
          updateSelectedDashboard(current => ({
            ...current,
            queries: current.queries.map(q => 
              q.id === query.id 
                ? {
                    ...q,
                    isLoading: false,
                    error: error.message
                  }
                : q
            )
          }));
          return null;
        }
      });

      await Promise.all(queryPromises);

    } catch (error) {
      setError(`Failed to load dashboard: ${error.message}`);
      setIsDashboardLoading(false);
    }
  }, [currentUser, updateSelectedDashboard, setError, setIsDashboardLoading, isAdmin]);

  // Function to handle creating a new dashboard
  const handleCreateDashboard = async (newDashboardName, selectedQueries, isShared = false) => {
    if (!currentUser) {
      setError('Please log in to create a new dashboard.');
      return;
    }
    try {
      const response = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/handleDashboardOperation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: { 
            operation: 'create',
            reportData: {
              name: newDashboardName,
              queries: selectedQueries,
              isShared: isShared
            },
            userId: currentUser.email
          }
        })
      });
      const data = await response.json();
      setDashboards(prevDashboards => [...prevDashboards, data]);
      handleSelectDashboard(data);
      return data;
    } catch (error) {
      setError('Failed to create dashboard. Please try again.');
    }
  };

  // Function to handle updating a dashboard
  const handleUpdateDashboard = async (updatedDashboard) => {
    try {
      const response = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/handleDashboardOperation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: { 
            operation: 'update',
            reportData: updatedDashboard,
            userId: currentUser.email,
            isAdmin: isAdmin
          }
        })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      handleSelectDashboard(updatedDashboard);
    } catch (error) {
      setError(`Failed to update dashboard: ${error.message}`);
    }
  };

  // Function to handle deleting a dashboard
  const handleDeleteDashboard = useCallback(async (dashboardId) => {
    if (!currentUser) {
      setError('Please log in to delete a dashboard.');
      return;
    }
    try {
      const response = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/handleDashboardOperation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: { 
            operation: 'delete',
            reportData: { id: dashboardId },
            userId: currentUser.email,
            isAdmin: isAdmin
          }
        })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await response.json();
      setDashboards(prevDashboards => prevDashboards.filter(d => d.id !== dashboardId));
      if (selectedDashboard && selectedDashboard.id === dashboardId) {
        clearSelectedDashboard();
      }
      // No need to manage preferences anymore
    } catch (error) {
      setError('Failed to delete dashboard. Please try again.');
    }
  }, [currentUser, selectedDashboard, clearSelectedDashboard, setDashboards, setError, isAdmin]);

  // Function to handle adding a query to a dashboard
  const handleAddQueryToDashboard = async (dashboardId, queryToAdd) => {
    try {
      const response = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/handleDashboardOperation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: { 
            operation: 'addQueryToDashboard',
            dashboardId: dashboardId,
            query: queryToAdd,
            userId: currentUser.email,
            isAdmin: isAdmin
          }
        })
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const updatedDashboard = await response.json();
  
      if (selectedDashboard && selectedDashboard.id === dashboardId) {
        // Add the new query with loading state
        const newQuery = {
          ...queryToAdd,
          isLoading: true,
          error: null,
          result: null,
          schema: null,
          cardSettings: {
            viewMode: 'table',
            chartType: 'bar',
            selectedXAxis: null,
            selectedYAxis: null,
            syncScales: false,
            sortColumn: null,
            sortOrder: 'asc'
          }
        };
  
        updateSelectedDashboard(current => ({
          ...current,
          ...updatedDashboard,
          queries: [...(current.queries || []), newQuery]
        }));
  
        // Execute the new query
        try {
          const queryData = await executeIndividualQuery(queryToAdd.id, dashboardId);
          
          updateSelectedDashboard(current => ({
            ...current,
            queries: current.queries.map(q => 
              q.id === queryToAdd.id 
                ? {
                    ...q,
                    ...queryData,
                    isLoading: false,
                    error: queryData.error || null
                  }
                : q
            )
          }));
        } catch (error) {
          updateSelectedDashboard(current => ({
            ...current,
            queries: current.queries.map(q => 
              q.id === queryToAdd.id 
                ? {
                    ...q,
                    isLoading: false,
                    error: error.message
                  }
                : q
            )
          }));
        }
      }
  
      await fetchDashboards();
      return true;
    } catch (error) {
      setError(`Failed to add query to dashboard: ${error.message}`);
      return false;
    }
  };

  const handleRefreshDashboard = useCallback(async (dashboard) => {
    if (!selectedDashboard) return;
    
    try {
      // First, refresh the dashboard structure
      const structureResponse = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/handleDashboardOperation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: { 
            operation: 'getDashboardStructure',
            reportData: { 
              dashboardId: dashboard.id,
              incrementRefreshCount: true
            },
            userId: currentUser.email,
            isAdmin: isAdmin
          }
        })
      });
  
      if (!structureResponse.ok) {
        throw new Error(`HTTP error! status: ${structureResponse.status}`);
      }
  
      const updatedStructure = await structureResponse.json();
  
      const queriesWithSettings = await Promise.all(
        updatedStructure.queries.map(async (query) => {
          try {
            const queryRef = doc(db, 'SavedQueries', query.id);
            const queryDoc = await getDoc(queryRef);
            
            if (queryDoc.exists()) {
              const queryData = queryDoc.data();
              return {
                ...query,
                userSettings: queryData.userSettings || {},
                creatorSettings: queryData.creatorSettings || {},
                userId: queryData.userId
              };
            }
            return query;
          } catch (error) {
            console.error(`Failed to fetch settings for query ${query.id}:`, error);
            return query;
          }
        })
      );

      // Initialize fresh query objects with loading states
      const initializedQueries = queriesWithSettings.map(query => {
        const existingQuery = selectedDashboard.queries.find(q => q.id === query.id);
        const userSpecificSettings = query.userSettings?.[currentUser.email];
        const creatorSettings = query.creatorSettings;
        
        // Use user settings if available, otherwise fall back to creator settings
        const effectiveSettings = userSpecificSettings || creatorSettings || {};
        
        const cardSettings = {
          viewMode: effectiveSettings.viewMode || existingQuery?.cardSettings?.viewMode || 'table',
          chartType: effectiveSettings.chartType || existingQuery?.cardSettings?.chartType || 'bar',
          selectedXAxis: effectiveSettings.selectedXAxis || existingQuery?.cardSettings?.selectedXAxis || null,
          selectedYAxis: effectiveSettings.selectedYAxis || existingQuery?.cardSettings?.selectedYAxis || null,
          syncScales: effectiveSettings.syncScales ?? existingQuery?.cardSettings?.syncScales ?? false,
          sortColumn: effectiveSettings.sortColumn || existingQuery?.cardSettings?.sortColumn || null,
          sortOrder: effectiveSettings.sortOrder || existingQuery?.cardSettings?.sortOrder || 'asc',
          cardWidth: effectiveSettings.cardWidth || existingQuery?.cardSettings?.cardWidth || '100%',
          visibleColumns: effectiveSettings.visibleColumns || existingQuery?.cardSettings?.visibleColumns || [],
          columnOrder: effectiveSettings.columnOrder || existingQuery?.cardSettings?.columnOrder || [],
          showSummary: effectiveSettings.showSummary ?? existingQuery?.cardSettings?.showSummary ?? true,
          activeFilters: effectiveSettings.activeFilters || existingQuery?.cardSettings?.activeFilters || {},
          showTrendLine: effectiveSettings.showTrendLine ?? existingQuery?.cardSettings?.showTrendLine ?? true,
          isNumberFormatted: effectiveSettings.isNumberFormatted ?? existingQuery?.cardSettings?.isNumberFormatted ?? true,
          transformationType: effectiveSettings.transformationType || existingQuery?.cardSettings?.transformationType || null,
          pivotConfig: effectiveSettings.pivotConfig || existingQuery?.cardSettings?.pivotConfig || null
        };
        
        return {
          ...query,
          isLoading: true,
          error: null,
          result: null,
          schema: null,
          cardSettings: cardSettings
        };
      });
  
      // Create fresh dashboard state
      const freshDashboard = {
        ...selectedDashboard,
        ...updatedStructure,
        queries: initializedQueries,
        lastRefreshed: updatedStructure.lastRefreshed,
        refreshCount: updatedStructure.refreshCount,
        _forceRefresh: Date.now()
      };
  
      // Update with fresh state
      updateSelectedDashboard(freshDashboard);
  
      // Execute all queries in parallel
      const queryPromises = initializedQueries.map(async (query) => {
        try {
          const queryData = await executeIndividualQuery(query.id, dashboard.id);
          
          // Update query data while preserving existing card settings
          updateSelectedDashboard(current => ({
            ...current,
            queries: current.queries.map(q => {
              if (q.id === query.id) {
                const existingSettings = q.cardSettings || {};
                return {
                  ...q,
                  ...queryData,
                  isLoading: false,
                  error: queryData.error || null,
                  cardSettings: {
                    ...existingSettings,
                    // Only set these if they don't already exist
                    visibleColumns: existingSettings.visibleColumns?.length 
                      ? existingSettings.visibleColumns 
                      : Object.keys(queryData.result?.[0] || {}),
                    columnOrder: existingSettings.columnOrder?.length
                      ? existingSettings.columnOrder
                      : Object.keys(queryData.result?.[0] || {})
                  }
                };
              }
              return q;
            })
          }));
  
          return queryData;
        } catch (error) {
          updateSelectedDashboard(current => ({
            ...current,
            queries: current.queries.map(q => 
              q.id === query.id 
                ? {
                    ...q,
                    isLoading: false,
                    error: error.message
                  }
                : q
            )
          }));
          return null;
        }
      });
  
      await Promise.all(queryPromises);
  
      // Update dashboards list with new refresh count and timestamp
      setDashboards(prevDashboards => 
        prevDashboards.map(d => 
          d.id === dashboard.id 
            ? {
                ...d,
                lastRefreshed: updatedStructure.lastRefreshed,
                refreshCount: updatedStructure.refreshCount
              }
            : d
        )
      );
  
    } catch (error) {
      setError('Failed to refresh dashboard. Please try again.');
    }
  }, [selectedDashboard, updateSelectedDashboard, currentUser, isAdmin, setError, setDashboards, executeIndividualQuery]);

  return {
    handleSelectDashboard,
    handleCreateDashboard,
    handleUpdateDashboard,
    handleAddQueryToDashboard,
    handleDeleteDashboard,
    handleRefreshDashboard,
    dashboardViewMode,
    updateDashboardViewMode,
    handleSaveCardSettings,
    executeIndividualQuery,
    saveQueryTitle
  };
};

export default DashboardManager;